import { lazy, Suspense, type ComponentType } from "react";
import type { CurrentSession, Role, Section } from "./domain";
import { RouteLoadBoundary, RouteLoading } from "./RouteLoadBoundary";
import { resolveRoleRoute } from "./roleSafeRoute";

// App.tsx limits navigation; this second boundary prevents a forbidden hash
// URL from mounting a sensitive module before the redirect effect runs.
const roleSections: Record<Role, readonly Section[]> = {
  admin: ["dashboard", "audit", "purchases", "sales", "catalog", "clients", "inventory", "finance", "retail", "operator_cash", "tasks", "settings"],
  manager: ["clients", "sales", "catalog", "inventory", "tasks"],
  operator: ["dashboard", "tasks", "purchases", "inventory", "operator_cash", "sales"],
  pharmacy: ["sales", "catalog", "inventory", "pharmacy_money"],
};

interface ModuleRouteProps {
  role: Role;
  mode: CurrentSession["mode"];
  userId: string;
}

type ModuleRoute = { default: ComponentType<ModuleRouteProps> };
type ModuleLoader = () => Promise<ModuleRoute>;

// Every page import stays behind the role gate below. Named exports are
// normalized for React.lazy without turning any page into a default export.
const moduleLoaders: Partial<Record<Section, ModuleLoader>> = {
  dashboard: () => import("./OperatorHomePage").then(({ OperatorHomePage }) => ({ default: ({ role }: ModuleRouteProps) => <OperatorHomePage role={role} /> })),
  audit: () => import("./AuditPage").then(({ AuditPage }) => ({ default: ({ role }: ModuleRouteProps) => <AuditPage role={role} /> })),
  clients: () => import("./CrmPage").then(({ CrmPage }) => ({ default: ({ role }: ModuleRouteProps) => <CrmPage role={role} /> })),
  catalog: () => import("./ProductsPage").then(({ ProductsPage }) => ({ default: ({ role, userId }: ModuleRouteProps) => <ProductsPage role={role} userId={userId} /> })),
  purchases: () => import("./ProcurementPage").then(({ ProcurementPage }) => ({ default: ({ role, userId }: ModuleRouteProps) => <ProcurementPage role={role} userId={userId} /> })),
  inventory: () => import("./InventoryPage").then(({ InventoryPage }) => ({ default: ({ role, mode }: ModuleRouteProps) => <InventoryPage role={role} mode={mode} /> })),
  sales: () => import("./SalesPage").then(({ SalesPage }) => ({ default: ({ role, userId }: ModuleRouteProps) => <SalesPage role={role} userId={userId} /> })),
  retail: () => import("./RetailPage").then(({ RetailPage }) => ({ default: ({ role, userId }: ModuleRouteProps) => <RetailPage role={role} userId={userId} /> })),
  pharmacy_money: () => import("./PharmacyMoneyPage").then(({ PharmacyMoneyPage }) => ({ default: ({ role, userId }: ModuleRouteProps) => <PharmacyMoneyPage role={role} userId={userId} /> })),
  operator_cash: () => import("./OperatorCashPage").then(({ OperatorCashPage }) => ({ default: ({ role, userId }: ModuleRouteProps) => <OperatorCashPage role={role} userId={userId} /> })),
  finance: () => import("./FinancePage").then(({ FinancePage }) => ({ default: ({ role, userId }: ModuleRouteProps) => <FinancePage role={role} userId={userId} /> })),
  tasks: () => import("./TasksPage").then(({ TasksPage }) => ({ default: ({ role, mode }: ModuleRouteProps) => <TasksPage role={role} mode={mode} /> })),
  settings: () => import("./SettingsPage").then(({ SettingsPage }) => ({ default: () => <SettingsPage /> })),
};

const lazyModules = new Map<ModuleLoader, ReturnType<typeof lazy<ComponentType<ModuleRouteProps>>>>();

function LazyModuleRoute({ loader, section, ...props }: ModuleRouteProps & { loader: ModuleLoader; section: Section }) {
  let Page = lazyModules.get(loader);
  if (!Page) { Page = lazy(loader); lazyModules.set(loader, Page); }
  return <RouteLoadBoundary resetKey={section}>
    <Suspense fallback={<RouteLoading />}>
      <Page {...props} />
    </Suspense>
  </RouteLoadBoundary>;
}

export function ModulePage({ section, role, mode = "demo", userId = "demo" }: { section: Exclude<Section, "dashboard"> | Section; role: Role; mode?: CurrentSession["mode"]; userId?: string }) {
  const loader = resolveRoleRoute(role, section, roleSections, moduleLoaders);
  if (!loader) return null;
  return <LazyModuleRoute loader={loader} section={section} role={role} mode={mode} userId={userId} />;
}
